import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { authClient } from './auth-client.js'

interface SessionUser {
  id: string
  email: string
}

interface AuthState {
  user: SessionUser | null
  loading: boolean
  signInWithPassword: (email: string, password: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  resendVerification: (email: string) => Promise<void>
  requestPasswordReset: (email: string) => Promise<void>
  resetPassword: (token: string, newPassword: string) => Promise<void>
  signOut: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [loading, setLoading] = useState(true)

  const loadSession = async () => {
    const { data } = await authClient.getSession()
    setUser(data?.user ? { id: data.user.id, email: data.user.email } : null)
  }

  useEffect(() => {
    loadSession().finally(() => setLoading(false))
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      signInWithPassword: async (email, password) => {
        const { error } = await authClient.signIn.email({ email, password })
        if (error) throw new Error(error.message ?? 'Sign in failed')
        await loadSession()
      },
      signInWithGoogle: async () => {
        // Better Auth runs the OAuth dance through the API Worker, then sends the
        // browser back to callbackURL on the SPA.
        await authClient.signIn.social({
          provider: 'google',
          callbackURL: `${window.location.origin}/auth/callback`,
        })
      },
      resendVerification: async (email) => {
        await authClient.sendVerificationEmail({
          email,
          callbackURL: `${window.location.origin}/onboarding/register`,
        })
      },
      requestPasswordReset: async (email) => {
        // redirectTo becomes the reset link's callbackURL: after Better Auth
        // validates the emailed token it bounces the browser here with ?token=…
        const { error } = await authClient.requestPasswordReset({
          email,
          redirectTo: `${window.location.origin}/reset-password`,
        })
        if (error) throw new Error(error.message ?? 'Could not send reset email')
      },
      resetPassword: async (token, newPassword) => {
        const { error } = await authClient.resetPassword({ newPassword, token })
        if (error) throw new Error(error.message ?? 'Could not reset password')
      },
      signOut: async () => {
        await authClient.signOut()
        setUser(null)
      },
      refresh: loadSession,
    }),
    [user, loading],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
